import React, { useCallback, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import {
  clearAgendaDatabookPin,
  getAgendaDatabookPin,
  getAgendaInputs,
  loadDatabookBlob,
  saveAgendaDatabookPin,
  saveAgendaInputs,
  type AgendaDatabookPin,
  type AgendaInputs,
  type AgendaSector,
} from "./storage";
import {
  detectQuestionColumns,
  getHeaderLabels,
  type RecognizedTab,
  type TabDetection,
} from "./detect";
import { extractQuestions, type ExtractionResult } from "./extract";
import { apiFetch } from "../../../api/fddClient";

type Props = {
  projectId: string;
  projectName?: string;
};

type LoadedWorkbook = {
  workbook: XLSX.WorkBook;
  detection: ReturnType<typeof detectQuestionColumns>;
};

type SectorOption = {
  id: AgendaSector;
  label: string;
  enabled: boolean;
};

const SECTORS: SectorOption[] = [
  { id: "midstream", label: "Midstream", enabled: true },
  { id: "renewables", label: "Renewables", enabled: true },
  { id: "solar-wind", label: "Solar & Wind", enabled: false },
  { id: "fiber", label: "Fiber", enabled: false },
  { id: "more", label: "More to come…", enabled: false },
];

const AgendaPanel: React.FC<Props> = ({ projectId, projectName }) => {
  const [pin, setPin] = useState<AgendaDatabookPin | null>(() => getAgendaDatabookPin(projectId));
  const [loaded, setLoaded] = useState<LoadedWorkbook | null>(null);
  const [extraction, setExtraction] = useState<ExtractionResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per-project inputs (sector + fiscal period + entities) for placeholder
  // substitution. Persists in localStorage so users don't re-enter every run.
  const [inputs, setInputs] = useState<AgendaInputs>(() => getAgendaInputs(projectId));
  const [generating, setGenerating] = useState(false);
  const [generateNote, setGenerateNote] = useState<string | null>(null);
  const [showAgendaConfirm, setShowAgendaConfirm] = useState(false);

  // Reload state when the active project changes.
  useEffect(() => {
    setPin(getAgendaDatabookPin(projectId));
    setInputs(getAgendaInputs(projectId));
    setLoaded(null);
    setExtraction(null);
    setError(null);
    setGenerateNote(null);
    setShowAgendaConfirm(false);
  }, [projectId]);

  // Sync the pin with the server-side databook store on every mount and
  // whenever projectId changes. If the server has a newer build than the
  // current pin (or there is no pin), re-pin automatically. This means
  // the agenda always reflects the latest Databook Builder output without
  // the user having to do anything in this tab.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const statusRes = await apiFetch(`/api/databook-store/${encodeURIComponent(projectId)}/status`).catch(() => null);
        const status = statusRes?.ok ? await statusRes.json().catch(() => null) : null;
        if (!status?.builtAt) return; // no server build exists yet

        const existingPin = getAgendaDatabookPin(projectId);
        const serverBuiltAt = new Date(status.builtAt).getTime();
        const pinTime = existingPin?.pinnedAt ? new Date(existingPin.pinnedAt).getTime() : 0;

        // Only re-pin when the server has a build that's newer than what we pinned.
        if (existingPin && serverBuiltAt <= pinTime) return;

        const buf = await loadDatabookBlob(projectId);
        if (!buf || cancelled) return;
        const workbook = XLSX.read(buf, { type: "array" });
        const detection = detectQuestionColumns(workbook);
        const questionColByTab: Record<string, number | null> = {};
        const agendaColByTab: Record<string, number | null> = {};
        const irlColByTab: Record<string, number | null> = {};
        const headerRowByTab: Record<string, number> = {};
        const sheetNameByTab: Record<string, string> = {};
        for (const t of detection.tabs) {
          questionColByTab[t.tab] = t.questionCol;
          agendaColByTab[t.tab] = t.agendaCol ?? null;
          irlColByTab[t.tab] = t.irlCol ?? null;
          headerRowByTab[t.tab] = t.headerRow;
          if (t.actualSheetName) sheetNameByTab[t.tab] = t.actualSheetName;
        }
        const newPin: AgendaDatabookPin = {
          source: "builder",
          fileName: status.sourceFilename || "databook.xlsx",
          pinnedAt: status.builtAt,
          fileSize: buf.byteLength,
          questionColByTab,
          agendaColByTab,
          irlColByTab,
          headerRowByTab,
          sheetNameByTab,
          detectionConfidence: detection.overallConfidence,
        };
        if (!cancelled) {
          saveAgendaDatabookPin(projectId, newPin);
          setPin(newPin);
          setLoaded(null);
          setExtraction(null);
        }
      } catch {
        // No server-side databook yet — stay unpinned until builder runs.
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  // Refresh the pin when the Databook Builder auto-pins a new build.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ projectId: string }>).detail;
      if (detail.projectId === projectId) {
        setPin(getAgendaDatabookPin(projectId));
        setLoaded(null);
        setExtraction(null);
      }
    };
    window.addEventListener("agenda-pin-updated", handler);
    return () => window.removeEventListener("agenda-pin-updated", handler);
  }, [projectId]);

  const updateInputs = useCallback((patch: Partial<AgendaInputs>) => {
    setInputs((prev) => {
      const next = { ...prev, ...patch };
      saveAgendaInputs(projectId, next);
      return next;
    });
  }, [projectId]);

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setGenerateNote(null);
    setError(null);
    try {
      const form = new FormData();
      if (pin) {
        const buf = await loadDatabookBlob(projectId);
        if (!buf) {
          setError("Databook bytes missing — re-pin and try again.");
          return;
        }
        const blob = new Blob([buf], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        form.append("databook", blob, pin.fileName);
        form.append("questionColByTab", JSON.stringify(pin.questionColByTab));
        form.append("headerRowByTab", JSON.stringify(pin.headerRowByTab));
        form.append("sheetNameByTab", JSON.stringify(pin.sheetNameByTab || {}));
        form.append("agendaColByTab", JSON.stringify(pin.agendaColByTab || {}));
      } else {
        form.append("questionColByTab", JSON.stringify({}));
        form.append("headerRowByTab", JSON.stringify({}));
        form.append("sheetNameByTab", JSON.stringify({}));
        form.append("agendaColByTab", JSON.stringify({}));
      }
      form.append("projectId", projectId);
      form.append("sector", inputs.sector);
      form.append("inputs", JSON.stringify({
        dealName: projectName,
        fiscalPeriod: inputs.fiscalPeriod,
        entities: inputs.entities,
        parentEntity: inputs.parentEntity,
        relatedParties: inputs.relatedParties,
      }));

      const res = await apiFetch("/api/agenda/generate", { method: "POST", body: form });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        setError(errBody?.error || `Generation failed (HTTP ${res.status})`);
        return;
      }
      const total = res.headers.get("X-Agenda-Total-Questions") || "0";
      const body = res.headers.get("X-Agenda-Body-Questions") || "0";
      const additional = res.headers.get("X-Agenda-Additional-Questions") || "0";
      const unrecognized = res.headers.get("X-Agenda-Unrecognized-Markers") || "";
      const aiResolved = res.headers.get("X-Agenda-Ai-Resolved") || "";

      const docxBlob = await res.blob();
      const url = URL.createObjectURL(docxBlob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date();
      const dateStr = `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`;
      const prefix = projectName ? `${projectName} - ` : "";
      a.download = `${prefix}FDD Agenda - ${dateStr}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const noteParts: string[] = [
        `Downloaded. ${total} trending question(s) found in the databook — ${body} placed in the agenda body, ${additional} listed under "Additional trending questions" at the end.`,
      ];
      if (aiResolved) noteParts.push(`AI resolved Questions column for: ${aiResolved}.`);
      if (unrecognized) noteParts.push(`Markers AI couldn't route to: ${unrecognized}`);
      setGenerateNote(noteParts.join(" "));
    } catch (e: any) {
      setError(e?.message || "Could not reach the agenda generator");
    } finally {
      setGenerating(false);
    }
  }, [pin, projectId, projectName, inputs]);

  // Whenever the pin changes, hydrate the workbook from IndexedDB and run
  // extraction so the UI can show the question count without a manual click.
  useEffect(() => {
    let cancelled = false;
    if (!pin) {
      setLoaded(null);
      setExtraction(null);
      return;
    }
    (async () => {
      setBusy(true);
      setError(null);
      try {
        const buf = await loadDatabookBlob(projectId);
        if (!buf) {
          setError("Pin metadata exists but the databook bytes are missing — re-pin a databook to fix.");
          setLoaded(null);
          setExtraction(null);
          return;
        }
        const workbook = XLSX.read(buf, { type: "array" });
        const detection = detectQuestionColumns(workbook);
        if (cancelled) return;
        setLoaded({ workbook, detection });
        // Use the pin's resolved columns (may include user overrides) for extraction.
        const result = extractQuestions(workbook, pin);
        setExtraction(result);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Failed to load pinned databook");
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, pin]);

  /* ────────────────── Override the column for a tab ────────────────── */
  const handleColumnOverride = (tab: RecognizedTab, col: number) => {
    if (!pin) return;
    const next: AgendaDatabookPin = {
      ...pin,
      questionColByTab: { ...pin.questionColByTab, [tab]: col },
      detectionConfidence: "user",
    };
    saveAgendaDatabookPin(projectId, next);
    setPin(next);
  };

  /* ────────────────── Clear pin ────────────────── */
  const handleClear = async () => {
    if (!pin) return;
    if (!window.confirm(`Unpin the databook "${pin.fileName}"? You can re-pin from the Databook Builder or by uploading a new one.`)) return;
    await clearAgendaDatabookPin(projectId);
    setPin(null);
    setLoaded(null);
    setExtraction(null);
  };

  /* ────────────────── Render ────────────────── */

  return (
    <section
      style={{
        border: "1px solid var(--theme-border)",
        borderRadius: 10,
        background: "var(--theme-tint-3)",
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >

      {/* Pin status */}
      {pin ? (
        <div
          style={{
            background: "#fff",
            border: "1px solid var(--theme-border)",
            borderRadius: 10,
            padding: 14,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontWeight: 700, color: "var(--theme-ink)", fontSize: 14 }}>
                📌 {pin.fileName}
              </div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                {pin.source === "builder" ? "Auto-pinned from Databook Builder" : "Manually uploaded"}
                {" · "}
                pinned {new Date(pin.pinnedAt).toLocaleString()}
                {pin.fileSize ? ` · ${formatBytes(pin.fileSize)}` : ""}
              </div>
            </div>
          </div>

          {/* Detection summary per tab */}
          {loaded && (
            <div
              style={{
                background: "var(--theme-tint-3)",
                border: "1px solid var(--theme-border)",
                borderRadius: 8,
                padding: 10,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <div style={{ fontWeight: 600, color: "var(--theme-ink)", fontSize: 12 }}>
                Detection — {confidenceLabel(pin.detectionConfidence)}
              </div>
              {loaded.detection.tabs.map((d) => (
                <TabDetectionRow
                  key={d.tab}
                  detection={d}
                  pinnedCol={pin.questionColByTab[d.tab] ?? null}
                  workbook={loaded.workbook}
                  onOverride={(col) => handleColumnOverride(d.tab, col)}
                />
              ))}
            </div>
          )}

          {/* Extraction results */}
          {extraction && (
            <div style={{ fontSize: 12, color: "var(--theme-ink-soft)", paddingTop: 4 }}>
              <strong style={{ color: "var(--theme-action)" }}>{extraction.totalQuestions}</strong>{" "}
              question{extraction.totalQuestions === 1 ? "" : "s"} extracted across{" "}
              {extraction.tabs.filter((t) => t.questions.length > 0).length} tab(s).
            </div>
          )}
        </div>
      ) : busy ? (
        <div style={{ padding: 14, background: "#fff", border: "1px dashed var(--theme-border)", borderRadius: 10, fontSize: 13, color: "var(--theme-ink-soft)" }}>
          Loading databook…
        </div>
      ) : (
        <div style={{ padding: 12, background: "#fff", border: "1px dashed var(--theme-border)", borderRadius: 8, fontSize: 12, color: "#6b7280" }}>
          No databook pinned — trending questions will be skipped. Build a databook first to include them.
        </div>
      )}

      {/* ── Agenda inputs + Generate — always visible ──────────────── */}
      <div
        style={{
          background: "var(--theme-tint-3)",
          border: "1px solid var(--theme-border)",
          borderRadius: 8,
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div style={{ fontWeight: 600, color: "var(--theme-ink)", fontSize: 12 }}>
          Build the agenda
        </div>

        {/* Sector selector */}
        <label style={{ fontSize: 12, color: "#374151", display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontWeight: 600 }}>Sector template</span>
          <select
            value={inputs.sector}
            onChange={(e) => updateInputs({ sector: e.target.value as AgendaSector })}
            style={{
              padding: "6px 8px",
              borderRadius: 6,
              border: "1px solid var(--theme-border)",
              fontSize: 12,
              background: "#fff",
            }}
          >
            {SECTORS.map((s) => (
              <option key={s.id} value={s.id} disabled={!s.enabled}>
                {s.label}{!s.enabled ? " — coming soon" : ""}
              </option>
            ))}
          </select>
        </label>

        {/* Fiscal period */}
        <label style={{ fontSize: 12, color: "#374151", display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontWeight: 600 }}>Fiscal period covered</span>
          <input
            type="text"
            value={inputs.fiscalPeriod}
            onChange={(e) => updateInputs({ fiscalPeriod: e.target.value })}
            placeholder="e.g., FY22 to Q3 FY24"
            style={{
              padding: "6px 8px",
              borderRadius: 6,
              border: "1px solid var(--theme-border)",
              fontSize: 12,
            }}
          />
        </label>

        {/* All transaction-perimeter entities — user lists them, then picks the parent */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
            Transaction-perimeter entities
          </span>
          {(inputs.entities.length === 0 ? [""] : inputs.entities).map((val, idx) => (
            <div key={idx} style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input
                type="text"
                value={val}
                onChange={(e) => {
                  const next = [...(inputs.entities.length === 0 ? [""] : inputs.entities)];
                  next[idx] = e.target.value;
                  const updated = next.filter((_, i) => i !== idx || e.target.value !== "");
                  const parentStillExists = updated.includes(inputs.parentEntity);
                  updateInputs({
                    entities: updated,
                    parentEntity: parentStillExists ? inputs.parentEntity : "",
                    relatedParties: updated.filter((e) => e && e !== (parentStillExists ? inputs.parentEntity : "")),
                  });
                }}
                placeholder={`Entity ${idx + 1}`}
                style={{ flex: 1, padding: "6px 8px", borderRadius: 6, border: "1px solid var(--theme-border)", fontSize: 12 }}
              />
              {inputs.entities.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    const updated = inputs.entities.filter((_, i) => i !== idx);
                    const parentStillExists = updated.includes(inputs.parentEntity);
                    updateInputs({
                      entities: updated,
                      parentEntity: parentStillExists ? inputs.parentEntity : "",
                      relatedParties: updated.filter((e) => e && e !== (parentStillExists ? inputs.parentEntity : "")),
                    });
                  }}
                  style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: "0 4px" }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => updateInputs({ entities: [...inputs.entities, ""] })}
            style={{ alignSelf: "flex-start", background: "none", border: "1px dashed var(--theme-border)", borderRadius: 6, color: "var(--theme-ink-soft)", cursor: "pointer", fontSize: 12, padding: "4px 10px", marginTop: 2 }}
          >
            + Add entity
          </button>
        </div>

        {/* Parent entity — selected from the list above */}
        {inputs.entities.filter(Boolean).length > 0 && (
          <label style={{ fontSize: 12, color: "#374151", display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontWeight: 600 }}>Which is the parent entity (seller)?</span>
            <select
              value={inputs.parentEntity}
              onChange={(e) => {
                const parent = e.target.value;
                updateInputs({
                  parentEntity: parent,
                  relatedParties: inputs.entities.filter((en) => en && en !== parent),
                });
              }}
              style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid var(--theme-border)", fontSize: 12, background: "#fff" }}
            >
              <option value="">— select parent —</option>
              {inputs.entities.filter(Boolean).map((en) => (
                <option key={en} value={en}>{en}</option>
              ))}
            </select>
          </label>
        )}

        {pin && extraction !== null && extraction.totalQuestions === 0 && (
          <div style={{ fontSize: 12, color: "#b7950b" }}>
            No trending questions found in the databook — generate the databook with trending analysis enabled first.
          </div>
        )}
        <button
          type="button"
          onClick={() => pin ? setShowAgendaConfirm(true) : void handleGenerate()}
          disabled={generating || busy}
          style={{
            background: generating ? "var(--theme-action-disabled)" : "var(--theme-action)",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "10px 14px",
            fontWeight: 700,
            cursor: generating ? "not-allowed" : "pointer",
            marginTop: 4,
          }}
        >
          {generating ? "Generating…" : "📝 Generate agenda"}
        </button>

        {generateNote && (
          <div style={{ fontSize: 11, color: "var(--theme-ink-soft)", paddingTop: 2 }}>
            {generateNote}
          </div>
        )}
      </div>

      {error && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fca5a5",
            borderRadius: 8,
            padding: "8px 12px",
            fontSize: 12,
            color: "#991b1b",
          }}
        >
          {error}
        </div>
      )}

      {showAgendaConfirm && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#fff", borderRadius: 12, padding: 24, maxWidth: 440, width: "90%",
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", gap: 14,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 15, color: "var(--theme-ink)" }}>Before you generate</div>
            <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6 }}>
              Make sure you've marked which trending questions should go to the agenda.
              Open the databook and put an <strong>x</strong> in the <strong>Agenda</strong> column
              next to each question you want included.
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Questions with no x in the Agenda column will be skipped.
              If your databook doesn't have an Agenda column yet, all questions will be included.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
              <button
                type="button"
                onClick={() => setShowAgendaConfirm(false)}
                style={{
                  background: "#fff", color: "var(--theme-ink-soft)", border: "1px solid var(--theme-border)",
                  borderRadius: 6, padding: "8px 16px", fontWeight: 600, fontSize: 13, cursor: "pointer",
                }}
              >
                Go back
              </button>
              <button
                type="button"
                onClick={() => { setShowAgendaConfirm(false); void handleGenerate(); }}
                style={{
                  background: "var(--theme-action)", color: "#fff", border: "none",
                  borderRadius: 8, padding: "8px 18px", fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}
              >
                I've marked it — generate
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

/* ─────────────────────────── Subcomponents ────────────────────────── */

const TabDetectionRow: React.FC<{
  detection: TabDetection;
  pinnedCol: number | null;
  workbook: XLSX.WorkBook;
  onOverride: (col: number) => void;
}> = ({ detection, pinnedCol, workbook, onOverride }) => {
  const [picking, setPicking] = useState(false);

  if (!detection.present) {
    return (
      <div style={{ fontSize: 12, color: "#6b7280", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontWeight: 600 }}>{detection.tab}</span>
        <span>— not present in this workbook</span>
      </div>
    );
  }

  const labels = getHeaderLabels(workbook, detection.tab, detection.headerRow);
  const pinnedLabel = pinnedCol != null ? labels.find((l) => l.col === pinnedCol)?.label ?? `Column ${colLetter(pinnedCol)}` : null;

  return (
    <div style={{ fontSize: 12, color: "#374151" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 600, color: "var(--theme-ink)", minWidth: 110 }}>{detection.tab}</span>
        {pinnedCol != null ? (
          <span>
            Questions in <strong>column {colLetter(pinnedCol)}</strong>
            {pinnedLabel ? <span style={{ color: "#6b7280" }}> ("{pinnedLabel}")</span> : null}
          </span>
        ) : (
          <span style={{ color: "#b7950b" }}>
            ⓘ No Questions column detected — pick one below.
          </span>
        )}
        <button
          type="button"
          onClick={() => setPicking((p) => !p)}
          style={{
            background: "transparent",
            color: "var(--theme-ink)",
            border: "1px solid var(--theme-border)",
            borderRadius: 6,
            padding: "2px 8px",
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {picking ? "Cancel" : pinnedCol != null ? "Change" : "Pick"}
        </button>
      </div>

      {picking && (
        <div style={{ marginTop: 6, paddingLeft: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
          {labels.map((l) => (
            <button
              key={l.col}
              type="button"
              onClick={() => {
                onOverride(l.col);
                setPicking(false);
              }}
              title={`Use column ${colLetter(l.col)}`}
              style={{
                background: l.col === pinnedCol ? "var(--theme-tint-21)" : "#fff",
                color: "var(--theme-ink)",
                border: `1px solid ${l.col === pinnedCol ? "var(--theme-accent)" : "var(--theme-border)"}`,
                borderRadius: 6,
                padding: "2px 8px",
                fontSize: 11,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {colLetter(l.col)} — {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────── Tiny utilities ─────────────────────── */

function colLetter(col1Based: number): string {
  let n = col1Based;
  let s = "";
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

function confidenceLabel(c: AgendaDatabookPin["detectionConfidence"]): React.ReactNode {
  switch (c) {
    case "exact":
      return <span style={{ color: "var(--theme-action)" }}>exact match (built by Databook Builder)</span>;
    case "heuristic":
      return <span style={{ color: "var(--theme-action)" }}>heuristic match</span>;
    case "user":
      return <span style={{ color: "var(--theme-action)" }}>user-confirmed</span>;
    case "unresolved":
      return <span style={{ color: "#b7950b" }}>needs your confirmation</span>;
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default AgendaPanel;
